import { useEffect, useMemo, useState } from "react";
import { http, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonthISO(year, month) {
  return `${year}-${String(month + 1).padStart(2, "0")}-01`;
}
function endOfMonthISO(year, month) {
  const last = new Date(year, month + 1, 0).getDate();
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
}
function fmtMonth(year, month) {
  return new Date(year, month, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default function History() {
  const { user } = useAuth();
  const [target, setTarget] = useState(user?.role === "admin" ? "adi" : user?.username);
  const [avatars, setAvatars] = useState({});
  const today = new Date();
  const [cursor, setCursor] = useState({ year: 2026, month: 6 }); // July 2026 (challenge start)
  const [days, setDays] = useState([]);
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayLogs, setDayLogs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/leaderboard");
        setAvatars({ adi: data.adi.avatar_url, kudrat: data.kudrat.avatar_url });
      } catch { /* non-critical: avatars just won't render */ }
    })();
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const start = startOfMonthISO(cursor.year, cursor.month);
        const end = endOfMonthISO(cursor.year, cursor.month);
        const [hist, h] = await Promise.all([
          http.get(`/history/${target}`, { params: { start, end } }),
          http.get(`/habits`, { params: { owner: target, date: end } }),
        ]);
        if (!cancel) {
          setDays(hist.data.days);
          setHabits(h.data.habits);
        }
      } catch (e) { toast.error(fmtErr(e)); }
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [target, cursor]);

  const dayByDate = useMemo(() => {
    const m = new Map();
    for (const d of days) m.set(d.date, d);
    return m;
  }, [days]);

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const startDow = (first.getDay() + 6) % 7; // Mon=0
    const last = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startDow; i++) arr.push(null);
    for (let d = 1; d <= last; d++) {
      const iso = `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      arr.push({ day: d, iso });
    }
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  async function openDay(iso) {
    setSelectedDate(iso);
    try {
      const { data } = await http.get(`/habits`, { params: { owner: target, date: iso } });
      setDayLogs(data.habits);
    } catch (e) { toast.error(fmtErr(e)); }
  }

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-display font-black text-3xl">Calendar</h1>
        <div className="flex items-center gap-2">
          <button className="nb-btn bg-white px-3 py-2" onClick={() => setCursor((c) => ({ year: c.month === 0 ? c.year - 1 : c.year, month: (c.month + 11) % 12 }))} data-testid="prev-month"><ChevronLeft size={16} /></button>
          <div className="chip bg-gold min-w-[160px] justify-center" data-testid="cursor-month">{fmtMonth(cursor.year, cursor.month)}</div>
          <button className="nb-btn bg-white px-3 py-2" onClick={() => setCursor((c) => ({ year: c.month === 11 ? c.year + 1 : c.year, month: (c.month + 1) % 12 }))} data-testid="next-month"><ChevronRight size={16} /></button>
          <button className="nb-btn bg-white px-3 py-2 text-xs" onClick={() => setCursor({ year: today.getFullYear(), month: today.getMonth() })}>Today</button>
        </div>
      </div>

      {user?.role === "admin" && (
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTarget("adi")} className={`nb-btn px-3 py-2 flex items-center gap-2 ${target === "adi" ? "bg-adi text-white" : "bg-white"}`} data-testid="hist-adi">
            {avatars.adi && <img src={avatars.adi} alt="Adi" className="w-6 h-6 rounded-full border-2 border-ink object-cover" />}
            Adi
          </button>
          <button onClick={() => setTarget("kudrat")} className={`nb-btn px-3 py-2 flex items-center gap-2 ${target === "kudrat" ? "bg-kudrat text-white" : "bg-white"}`} data-testid="hist-kudrat">
            {avatars.kudrat && <img src={avatars.kudrat} alt="Kudrat" className="w-6 h-6 rounded-full border-2 border-ink object-cover" />}
            Kudrat
          </button>
        </div>
      )}

      <div className="nb-card p-4 md:p-6">
        <div className="grid grid-cols-7 gap-2 mb-2">
          {DAYS.map((d) => (
            <div key={d} className="text-center text-[10px] font-mono uppercase tracking-widest text-neutral-500">{d}</div>
          ))}
        </div>
        {loading ? <div className="text-center py-8 text-neutral-500">Loading…</div> : (
          <div className="grid grid-cols-7 gap-2" data-testid="calendar-grid">
            {cells.map((c, i) => {
              if (!c) return <div key={`pad-${cursor.year}-${cursor.month}-${i}`} />;
              const info = dayByDate.get(c.iso);
              const pct = info && info.max > 0 ? Math.min(1, info.earned / info.max) : 0;
              const bg = pct >= 1 ? "#10B981" : pct >= 0.7 ? "#FBBF24" : pct >= 0.4 ? "#FED7AA" : pct > 0 ? "#FECDD3" : "#ffffff";
              const isToday = c.iso === today.toISOString().slice(0, 10);
              return (
                <button key={c.iso} onClick={() => openDay(c.iso)}
                  className={`nb-card p-2 sm:p-3 aspect-square flex flex-col items-center justify-center text-center hover:-translate-y-0.5 ${isToday ? "ring-4 ring-gold" : ""}`}
                  style={{ background: bg }}
                  data-testid={`cal-day-${c.iso}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest">{c.day}</div>
                  {info ? (
                    <>
                      <div className="font-display font-black text-lg md:text-xl leading-tight mt-1">{Math.round(info.earned)}</div>
                      <div className="text-[10px] text-neutral-700">/ {Math.round(info.max)}</div>
                    </>
                  ) : (
                    <div className="text-[10px] text-neutral-400 mt-1">—</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!selectedDate} onOpenChange={(v) => !v && setSelectedDate(null)}>
        <DialogContent className="border-2 border-ink rounded-3xl shadow-[0_12px_0_0_#171717] bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">{selectedDate && new Date(selectedDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</DialogTitle>
            <DialogDescription>{target}'s habits on this day</DialogDescription>
          </DialogHeader>
          <div className="mt-2 max-h-[60vh] overflow-y-auto space-y-2">
            {dayLogs.length === 0 && <div className="text-neutral-500">No habits active on this day.</div>}
            {dayLogs.map((h) => (
              <div key={h.id} className="flex items-center justify-between border-b border-dashed border-neutral-300 py-2 gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{h.name}</div>
                  {h.log?.marked_at && <div className="text-[10px] font-mono text-neutral-500">Marked {new Date(h.log.marked_at).toLocaleString()}</div>}
                </div>
                {h.log ? (
                  <span className={`chip ${h.log.status === "full" ? "bg-emerald-100" : h.log.status === "partial" ? "bg-yellow-100" : "bg-rose-100"}`}>
                    {h.log.status} · {h.log.points}
                  </span>
                ) : <span className="chip bg-neutral-100">not marked</span>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
